// Coherent, cross-referenced asset content for Hero/CTA animation
// Two members (A/B). Doc snippets contain highlight phrases. Summary sentences
// reference those highlights. Demo signals (Add/Suspect/Delete) reference
// summary sentence ids for rationale mapping (to be wired during interactivity).

export type MemberId = "A" | "B";

export type DocSnippetContent = {
  id: string;
  memberId: MemberId;
  title: string; // e.g., "Alice Nguyen"
  text: string; // raw chart text
  highlights: string[]; // phrases to emphasize during scroll
};

export type SummarySentence = {
  id: string;
  text: string;
  // optional: which doc highlight it maps to (string match for now)
  sourceHighlight?: string;
};

export type SummaryContent = {
  id: string;
  memberId: MemberId;
  title: string; // visit context / date
  sentences: SummarySentence[];
};

export type ChecklistStep = {
  label: string;
  key: "ocr" | "analyzing" | "identifying" | "compiling";
};

export type DemoSignalType = "add" | "suspect" | "delete";

export type DemoRationale = {
  id: string;
  text: string;
  // which summary sentence ids this rationale ties to
  summaryRefs?: string[];
};

export type DemoSignal = {
  id: string;
  memberId: MemberId;
  kind: DemoSignalType;
  title: string; // e.g., "Add Dx" | "Suspected Dx" | "Delete Dx"
  code: string; // e.g., "E11.9 | HCC 38"
  badgeLabel?: string;
  badgeIcon?: "check" | "exclamation";
  rationale: DemoRationale[];
  // UI customizations for hero demo cards
  showActions?: boolean;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  penaltyText?: string;
  meatValidatedLetters?: Array<"M" | "E" | "A" | "T">;
  secondarySectionTitle?: string;
  secondarySectionText?: string;
  bottomTagText?: string;
};

export type HeroAssets = {
  docSnippets: DocSnippetContent[];
  summaries: SummaryContent[];
  checklist: ChecklistStep[];
  demoSignals: DemoSignal[];
};

// Synthetic longitudinal chart generators to produce very long, realistic notes
const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const monthsBetween = (startYear: number, startMonth: number, endYear: number, endMonth: number) => {
  const out: Array<{ y: number; m: number }> = [];
  let y = startYear;
  let m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    out.push({ y, m });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
};

function makeRecordA(): string {
  const header =
    "Problem List: T2DM (dx 2010); HFrEF (EF 38%); CKD 3b (albuminuric); HTN; HLD; COPD (stable); Obesity (BMI ~34)\n" +
    "Allergies: NKDA\n" +
    "Medications: metformin 1000 mg bid; ACEi; statin; torsemide [20 mg qd]; PRN albuterol; vitamin D\n\n";

  // Anchor encounters with highlight strings
  const anchors =
    "03-14-2022 · PRIMARY CARE · J. Smith, DO · Riverside IM\n" +
    "CC: diabetes follow-up; mild exertional dyspnea\n" +
    "PMH: T2DM (since 2010); HTN; HLD; COPD (stable)\n" +
    "HPI: home glucose 140–180; diet inconsistent; 5lb weight gain; orthopnea 2 pillows\n" +
    "EXAM: BP 146/86; HR 82; Wt 99 kg; basilar rales\n" +
    "LABS: A1c [8.7% (10/24)]; Cr 1.58; eGFR [42]; UACR [280 mg/g (11/24)]\n" +
    "PLAN: reinforce diet; add SGLT2 consideration; salt restriction; daily weights; f/u cardiology\n\n" +
    "07-22-2022 · CARDIOLOGY · M. Alvarez, MD · Heart Center\n" +
    "CC: dyspnea on exertion; ankle swelling\n" +
    "ECHO: EF 38%; mild LV dilation; no pericardial effusion\n" +
    "ASSESS: HFrEF NYHA II; mild volume overload\n" +
    "PLAN: ↑ loop diuretic → torsemide [20 mg qd]; sodium <2g; monitoring; f/u 1 mo\n\n" +
    "01-18-2023 · NEPHROLOGY · R. Singh, MD · Renal Clinic\n" +
    "CC: CKD follow-up\n" +
    "LABS: Cr 1.62; eGFR [42]; UACR [280 mg/g (11/24)]\n" +
    "ASSESS: CKD 3b likely diabetic + hypertensive; albuminuria persistent\n" +
    "PLAN: continue ACEi; encourage SGLT2; BP goal <130/80; avoid NSAIDs\n\n" +
    "01-12-2024 · PRIMARY CARE · J. Smith, DO\n" +
    "CC: shortness of breath; diabetes follow-up\n" +
    "HPI: T2DM (poor control); HFrEF; CKD 3b; HTN; HLD; COPD (stable)\n" +
    "EXAM: BP 148/88; HR 86; Wt 101 kg (BMI 34)\n" +
    "FINDINGS: +1 ankle edema; basilar rales; JVP mildly ↑\n" +
    "PLAN: continue torsemide [20 mg qd]; low-salt; daily weights; SGLT2 prior auth re-submitted\n\n";

  const pcpT = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, Math.max(1, m - 1)))}-${y} · PRIMARY CARE · J. Smith, DO\n` +
    // Include recurring highlight phrases in non-anchor PCP blocks
    "HPI: T2DM adherence reviewed; no hypoglycemia; DOE on stairs mild; denies chest pain; orthopnea 2 pillows.\n" +
    // Repeat A1c highlight exactly so it can be targeted anywhere in the timeline
    "LABS: A1c [8.7% (10/24)].\n" +
    "EXAM: BP borderline; weight stable; lungs clear; trace ankle edema.\n" +
    // Repeat torsemide exact phrase for highlight targeting
    "PLAN: reinforce diet + sodium restriction; continue torsemide [20 mg qd]; refills; repeat A1c next draw.\n";
  const cardioT = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m))}-${y} · CARDIOLOGY · Heart Center\n` +
    // These already contain highlight phrases ("EF 38%" and "HFrEF") that repeat
    "ECHO: EF 38% unchanged; no significant valvular disease.\n" +
    "ASSESS: HFrEF NYHA II; euvolemic today.\n" +
    "PLAN: continue GDMT; monitor daily weights; sodium <2g reinforced.\n";
  const nephroT = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 1))}-${y} · NEPHROLOGY · Renal Clinic\n` +
    // Replace generic labs with exact highlight phrases so they repeat in non-anchor blocks
    "LABS: Cr 1.6–1.7; eGFR [42]; UACR [280 mg/g (11/24)].\n" +
    "ASSESS: CKD 3b likely hypertensive + diabetic etiology.\n" +
    "PLAN: continue ACEi; consider SGLT2; avoid NSAIDs; hydration counseling.\n";
  const pulmT = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 2))}-${y} · PULMONOLOGY · Valley Pulmonary\n` +
    "HPI: COPD (stable); rescue inhaler 1–2x/week; no nocturnal symptoms.\n" +
    "EXAM: mild scattered wheeze; no distress.\n" +
    "PLAN: spacer technique reinforced; continue LAMA/LABA; rehab adherence reviewed.\n";
  const cmT1 = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 5))}-${y} · CARE MANAGEMENT · RN outreach\n` +
    "Notes: reviewed BP/weight logs; reinforced low-sodium diet; symptom diary updated; adherence verified.\n";
  const cmT2 = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 7))}-${y} · PATIENT PORTAL MESSAGE\n` +
    "Msg: refill request for torsemide; reports improved breathing with consistent diuretic adherence.\n";

  const timeline = monthsBetween(2019, 1, 2025, 12);
  const lines: string[] = [];
  lines.push(header);
  lines.push(anchors);
  for (let i = 0; i < timeline.length; i++) {
    const { y, m } = timeline[i];
    switch (i % 10) {
      case 0:
      case 5:
        lines.push(pcpT(y, m));
        break;
      case 1:
        lines.push(cardioT(y, m));
        break;
      case 2:
        lines.push(nephroT(y, m));
        break;
      case 3:
      case 8:
        lines.push(pulmT(y, m));
        break;
      case 6:
        lines.push(cmT1(y, m));
        break;
      default:
        lines.push(cmT2(y, m));
        break;
    }
  }
  return lines.join("\n");
}

function makeRecordB(): string {
  const header =
    "Problem List: COPD (stable); HTN; HLD; T2DM (diet-controlled); former smoker (quit 2015)\n" +
    "Allergies: NKDA\n" +
    "Medications: LAMA/LABA; albuterol inhaler PRN; ACEi; statin\n\n";

  const anchors =
    "10-05-2022 · URGENT CARE · Acute visit\n" +
    "CC: cough with sputum, low-grade fever\n" +
    "ASSESS: COPD exacerbation (10/24) — resolved with supportive care\n\n" +
    "12-28-2023 · PULMONOLOGY · L. Chen, MD · Valley Pulmonary\n" +
    "CC: COPD follow-up; dyspnea on exertion\n" +
    "HPI: COPD (stable); HTN; HLD; T2DM (diet control); no recent exacerbations.\n" +
    "EXAM: scattered wheeze; no respiratory distress\n" +
    "LABS: Cr [1.73 mg/dL]; eGFR [43]; UACR [190 mg/g]\n" +
    "PRIOR DX: COPD exacerbation (10/24) — resolved\n" +
    "PLAN: continue LAMA/LABA; reinforce inhaler technique; BMP/UA next visit\n\n";

  const pcpT = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, Math.max(1, m - 2)))}-${y} · PRIMARY CARE · L. Howard, MD\n` +
    // Include highlight phrases in non-anchor PCP blocks so they repeat
    "HPI: COPD (stable); O2 94–95% at rest; activity tolerance stable; trigger avoidance reviewed; no recent exacerbations.\n" +
    "PLAN: continue maintenance regimen; spacer technique re-educated; labs per schedule.\n";
  const pulmT = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 1))}-${y} · PULMONOLOGY · L. Chen, MD\n` +
    "HPI: seasonal cough improved; rescue inhaler infrequent; no nighttime symptoms.\n" +
    // Repeat prior resolved event exact phrase for highlight targeting
    "PRIOR: COPD exacerbation (10/24) — resolved.\n" +
    "EXAM: mild end-expiratory wheeze; no distress.\n" +
    "PLAN: continue LAMA/LABA; consider rehab refresher; inhaler technique reinforced.\n";
  const cmT1 = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 6))}-${y} · CARE MANAGEMENT · RN outreach\n` +
    "Notes: reviewed adherence; encouraged daily walking; denies ER visits; no recent exacerbations.\n" +
    // Repeat the exact bracketed lab highlight phrases
    "Recent labs: Cr [1.73 mg/dL]; eGFR [43]; UACR [190 mg/g].\n";
  const cmT2 = (y: number, m: number) =>
    `${pad2(m)}-${pad2(Math.min(28, m + 9))}-${y} · PATIENT PORTAL MESSAGE\n` +
    "Msg: inhaler refill requested; reports good control; no nighttime symptoms.\n";

  const timeline = monthsBetween(2022, 1, 2025, 12);
  const lines: string[] = [];
  lines.push(header);
  lines.push(anchors);
  for (let i = 0; i < timeline.length; i++) {
    const { y, m } = timeline[i];
    switch (i % 8) {
      case 0:
      case 4:
        lines.push(pcpT(y, m));
        break;
      case 1:
      case 5:
        lines.push(pulmT(y, m));
        break;
      case 2:
        lines.push(cmT1(y, m));
        break;
      default:
        lines.push(cmT2(y, m));
        break;
    }
  }
  return lines.join("\n");
}
// Clinical scenario chosen to be coherent for MAO risk adjustment review.
// Member A: Poorly controlled T2DM with CKD 3b and HFrEF symptoms.
// Member B: COPD stable; suspected CKD progression; one previously coded Dx to delete.

export const heroAssets: HeroAssets = {
  docSnippets: [
    {
      id: "doc-A-01",
      memberId: "A",
      title: "Alice Nguyen",
      text: makeRecordA(),
      highlights: [
        "orthopnea 2 pillows",
        "A1c [8.7% (10/24)]",
        "eGFR [42]",
        "UACR [280 mg/g (11/24)]",
        "torsemide [20 mg qd]",
        "EF 38%",
        "HFrEF",
        "CKD 3b",
      ],
    },
    {
      id: "doc-B-01",
      memberId: "B",
      title: "Robert Johnson",
      text: makeRecordB(),
      highlights: [
        "Cr [1.73 mg/dL]",
        "eGFR [43]",
        "UACR [190 mg/g]",
        "COPD exacerbation (10/24) — resolved",
        "COPD (stable)",
        "no recent exacerbations",
      ],
    },
  ],

  summaries: [
    {
      id: "sum-A-01",
      memberId: "A",
      title: "",
      sentences: [
        { id: "sum-A-01-s1", text: "Poorly controlled T2DM with A1c 8.7%.", sourceHighlight: "A1c 8.7% (10/24)" },
        { id: "sum-A-01-s2", text: "CKD stage 3b supported by eGFR 42 and UACR 280 mg/g.", sourceHighlight: "eGFR [42]" },
        { id: "sum-A-01-s3", text: "HFrEF symptoms: orthopnea (2 pillows), mild overload; diuretic uptitrated.", sourceHighlight: "orthopnea 2 pillows" },
      ],
    },
    {
      id: "sum-B-01",
      memberId: "B",
      title: "",
      sentences: [
        { id: "sum-B-01-s1", text: "COPD stable, no recent exacerbations." },
        { id: "sum-B-01-s2", text: "Renal markers suggest CKD progression (eGFR 43, UACR 190 mg/g).", sourceHighlight: "eGFR [43]" },
        { id: "sum-B-01-s3", text: "Prior COPD exacerbation (10/24) has resolved; should not be carried forward.", sourceHighlight: "COPD exacerbation (10/24) — resolved" },
      ],
    },
  ],

  checklist: [
    { key: "ocr", label: "Ingesting charts" },
    { key: "analyzing", label: "Analyzing data" },
    { key: "identifying", label: "Identifying codes" },
    { key: "compiling", label: "Compiling deltas" },
  ],

  demoSignals: [
    // Member A: Add Dx (E11.9 | HCC 38)
    {
      id: "demo-A-add",
      memberId: "A",
      kind: "add",
      title: "Add Dx",
      code: "E11.9 | HCC 38",
      badgeLabel: "RADV-defensible",
      penaltyText: "+$162.68 PMPM",
      meatValidatedLetters: ["M", "A"],
      rationale: [
        { id: "r-A-1", text: "A1c 8.7% indicates poor control.", summaryRefs: ["sum-A-01-s1"] },
        { id: "r-A-2", text: "Consistent longitudinal documentation of T2DM." },
      ],
      bottomTagText: "Final run"
    },
    // Member B: Suspected Dx (CKD 3b)
    {
      id: "demo-B-suspect",
      memberId: "B",
      kind: "suspect",
      title: "Suspected Dx",
      code: "N18.32 | HCC 136",
      showActions: false,
      meatValidatedLetters: [],
      secondarySectionTitle: "Suggested prospective actions",
      secondarySectionText: "Schedule nephrology follow-up.",
      // secondarySectionText: "Repeat labs (A1c, UACR, BMP). Consider SGLT2 initiation. Schedule nephrology consult.",
      penaltyText: "+$124.46 PMPM",
      rationale: [
        { id: "r-B-1", text: "eGFR 43 and UACR 190 mg/g suggest CKD 3b.", summaryRefs: ["sum-B-01-s2"] },
        // { id: "r-B-2", text: "Recommend confirmatory labs and nephrology follow-up." },
      ],
    },
    // Member B: Delete Dx (remove resolved acute bronchitis)
    {
      id: "demo-B-delete",
      memberId: "B",
      kind: "delete",
      title: "Delete Dx",
      code: "J44.1 | HCC 111",
      badgeLabel: "RADV-exposure",
      badgeIcon: "exclamation",
      primaryActionLabel: "Delete Dx",
      secondaryActionLabel: "Ignore",
      penaltyText: "$312.62 PMPM penalty",
      meatValidatedLetters: [],
      rationale: [
        { id: "r-B-3", text: "Prior COPD exacerbation resolved; do not carry forward.", summaryRefs: ["sum-B-01-s3"] },
      ],
    },
  ],
};

export default heroAssets;


